/* @jsx h */
import { defineComponent, ref, onMounted, computed, h } from "vue";
import { api } from "@app/data/index";
import { Product, Extra } from "@lemmonhq/types";
import { Loading } from "@app/vue/components/index";
import { setTitle } from "@lemmonhq/orders";
import { ProductsTable } from "./ProductsTable";
import { useToast } from "../../providers";
import { useLocalization } from "@lemmonhq/uikit";

type CatalogItem = {
  id: number;
  name: string;
  type: "product" | "extra";
  price?: string;
  status: string;
  item: Product | Extra;
};

export const ProductsPage = defineComponent({
  name: "ProductsPage",
  setup() {
    const catalogItems = ref<CatalogItem[]>([]);
    const loading = ref(true);
    const error = ref<string | null>(null);
    const searchQuery = ref("");
    const toast = useToast();
    const { t } = useLocalization();

    onMounted(() => {
      setTitle("Products");
      fetchProducts();
    });

    const fetchProducts = async () => {
      try {
        const [products, extras] = await Promise.all([
          api.getProducts(),
          api.getExtras(),
        ]);

        // Combine products and extras into a single list
        catalogItems.value = [
          ...products.map(
            (product): CatalogItem => ({
              id: product.id,
              name: product.name,
              type: "product",
              price: product.price,
              status: product.status,
              item: product,
            }),
          ),
          ...extras.map(
            (extra): CatalogItem => ({
              id: extra.id,
              name: extra.title,
              type: "extra",
              status: extra.status,
              item: extra,
            }),
          ),
        ];

        error.value = null;
      } catch (err) {
        error.value = "Failed to load catalog items";
        console.error("Error fetching catalog items:", err);
      } finally {
        loading.value = false;
      }
    };

    const handleToggleStatus = async (
      itemId: number,
      itemType: "product" | "extra",
      isEnabled: boolean,
    ) => {
      try {
        const resp =
          itemType === "product"
            ? await api.updateProductStatus(itemId, isEnabled)
            : await api.updateExtraStatus(itemId, isEnabled);

        // Update local state
        catalogItems.value = catalogItems.value.map((item) =>
          item.id === itemId && item.type === itemType
            ? { ...item, ...resp }
            : item,
        );
      } catch (err) {
        console.error("Error updating status:", err);
        // Revert the toggle if the API call fails
        toast.showToast("Unable to update " + itemType, {
          type: "error",
          icon: "fork_spoon",
        });
      }
    };

    const filteredItems = computed(() =>
      searchQuery.value
        ? catalogItems.value.filter((item) =>
            item.name.toLowerCase().includes(searchQuery.value.toLowerCase()),
          )
        : catalogItems.value,
    );

    return () => {
      if (loading.value) {
        return (
          <div class="w-full h-full flex-1 flex justify-center items-center">
            <Loading />
          </div>
        );
      }

      if (error.value) {
        return (
          <div class="p-8">
            <div
              class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
              role="alert"
            >
              <p class="font-bold">Error</p>
              <p>{error.value}</p>
            </div>
          </div>
        );
      }

      return (
        <div class="p-2 lg:p-4 xl:p-4 h-full flex flex-col">
          <div class="mb-4">
            <label class="input input-bordered flex items-center gap-2 relative">
              <span class="absolute left-3 top-1/2 transform -translate-y-1/2  h-5 w-5 flex justify-start items-center label-text">
                <span class="material-symbols-outlined mr-1 text-2xl">
                  manage_search
                </span>
              </span>
              <input
                type="text"
                v-model={searchQuery.value}
                placeholder={t("products.core.search_placeholder")}
                class="grow pl-6"
              />
            </label>
          </div>
          <div class="flex-1 overflow-auto">
            <ProductsTable
              items={filteredItems.value}
              onToggleStatus={handleToggleStatus}
            />
          </div>
        </div>
      );
    };
  },
});
